import { useEffect, useState } from "react";
import { CalendarDays, Plus, Pencil, Trash2, User, School } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const todayStr = () => new Date().toISOString().slice(0, 10);

export const lessonDateLabel = (l, lang) =>
  new Date(`${l.date}T${l.time || "00:00"}`).toLocaleDateString(dateLocale(lang), { weekday: "short", day: "numeric", month: "short" });

// Ligne d'affichage d'un cours (partagée par l'agenda enseignant et les fiches)
export function LessonRow({ l, lang, t, canEdit, onEdit, onDelete }) {
  return (
    <div className="app-card px-4 py-3 flex items-center gap-3" data-testid={`lesson-row-${l.id}`}>
      <div className="shrink-0 w-14 text-center">
        <p className="text-xs t-secondary capitalize">{lessonDateLabel(l, lang)}</p>
        <p className="text-sm font-bold">{l.time || "—"}</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate flex items-center gap-1.5">
          {l.type === "individual" ? <User className="h-3.5 w-3.5 accent shrink-0" /> : <School className="h-3.5 w-3.5 brand shrink-0" />}
          {l.target_name}
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/5 border border-app t-secondary">
            {t(l.type === "individual" ? "teach.lesson_individual" : "teach.lesson_class")}
          </span>
        </p>
        <p className="text-xs t-secondary">{t("teach.minutes", { n: l.duration_minutes })}{l.notes ? ` — ${l.notes}` : ""}</p>
      </div>
      {canEdit && (
        <div className="flex gap-1 shrink-0">
          <button onClick={() => onEdit(l)} className="t-secondary hover:text-white p-1.5" data-testid={`lesson-edit-${l.id}`} aria-label={t("teach.edit_lesson")}>
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(l)} className="t-secondary hover:text-rose-400 p-1.5" data-testid={`lesson-delete-${l.id}`} aria-label={t("teach.delete_lesson")}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// Agenda pédagogique de l'enseignant : cours individuels + cours de classe (Phase 2)
export default function AgendaTeachingPage() {
  const { lang, t } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delLesson, setDelLesson] = useState(null);
  const emptyForm = { type: "individual", student_relation_id: "", class_id: "", date: todayStr(), time: "", duration_minutes: 60, notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/teaching/lessons").then((r) => setLessons(r.data.lessons)).catch(() => {});
    api.get("/teaching/students").then((r) => setStudents(r.data.students.filter((s) => s.status === "ACTIVE"))).catch(() => {});
    api.get("/teaching/classes").then((r) => setClasses(r.data.classes)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ type: l.type, student_relation_id: l.student_relation_id || "", class_id: l.class_id || "", date: l.date, time: l.time || "", duration_minutes: l.duration_minutes, notes: l.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/teaching/lessons/${editing.id}`, form);
      } else {
        await api.post("/teaching/lessons", form);
      }
      toast.success(t("teach.lesson_created"));
      setOpen(false);
      load();
    } catch (e) { toast.error(e.friendly); }
    setSaving(false);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/teaching/lessons/${delLesson.id}`);
      setDelLesson(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const today = todayStr();
  const upcoming = lessons.filter((l) => l.date >= today);
  const past = lessons.filter((l) => l.date < today).reverse();
  const formValid = form.date && (form.type === "individual" ? form.student_relation_id : form.class_id);

  return (
    <div className="space-y-4 max-w-3xl" data-testid="teach-agenda-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <CalendarDays className="h-6 w-6 brand" /> {t("teach.nav_agenda")}
        </h2>
        <Button className="brand-bg text-white" onClick={openCreate} data-testid="add-lesson-button">
          <Plus className="h-4 w-4 mr-1" /> {t("teach.add_lesson")}
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider t-secondary">{t("teach.upcoming_lessons")}</p>
        {upcoming.length === 0 ? (
          <p className="text-sm t-secondary py-4 text-center" data-testid="lessons-empty">{t("teach.no_lessons")}</p>
        ) : (
          upcoming.map((l) => <LessonRow key={l.id} l={l} lang={lang} t={t} canEdit onEdit={openEdit} onDelete={setDelLesson} />)
        )}
      </div>

      {past.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider t-secondary">{t("teach.past_lessons")}</p>
          {past.map((l) => <LessonRow key={l.id} l={l} lang={lang} t={t} canEdit onEdit={openEdit} onDelete={setDelLesson} />)}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-bg border-app" data-testid="lesson-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t(editing ? "teach.edit_lesson" : "teach.add_lesson")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider t-secondary mb-1.5">{t("teach.lesson_type")}</p>
              <div className="flex gap-2" data-testid="lesson-type-chips">
                {[["individual", "teach.lesson_individual"], ["class", "teach.lesson_class"]].map(([v, l]) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setForm({ ...form, type: v })}
                    disabled={!!editing}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${form.type === v ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                    data-testid={`lesson-type-${v}`}
                  >
                    {t(l)}
                  </button>
                ))}
              </div>
            </div>
            {form.type === "individual" ? (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_student")}</Label>
                <Select value={form.student_relation_id} onValueChange={(v) => setForm({ ...form, student_relation_id: v })} disabled={!!editing}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="lesson-student-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_class_label")}</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })} disabled={!!editing}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="lesson-class-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_date")}</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="lesson-date-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_time")}</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="lesson-time-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_duration")}</Label>
                <Input type="number" min="5" step="5" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} className="mt-1 bg-transparent border-app" data-testid="lesson-duration-input" />
              </div>
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.lesson_notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 bg-transparent border-app" data-testid="lesson-notes-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={save} disabled={!formValid || saving} data-testid="lesson-save-confirm">
              {t(editing ? "common.save" : "common.create")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delLesson}
        title={t("teach.delete_lesson")}
        description={t("teach.delete_lesson_desc")}
        onConfirm={doDelete}
        onClose={() => setDelLesson(null)}
        testid="lesson-delete-confirm"
      />
    </div>
  );
}
